/**
 * Parse a single labeled transcript into per-speaker segments.
 *
 * Handles the common "Name: text" export shape (Zoom, manual paste, and most
 * Fireflies text exports), with optional leading timestamps:
 *
 *   [00:12] Ada Lovelace: Thanks for joining.
 *   Alan Turing: Quick question on scope.
 *   Grace Hopper (05:05): Fireflies "copy transcript" puts the time after the name.
 *   Ada Lovelace: Good point — let's dig in.
 *
 * Rules:
 *   - A line starting with `Name:` opens a new turn for that speaker. The name
 *     must look like a name (letters/spaces/.'- , ≤ 4 words, ≤ 40 chars) so a
 *     mid-sentence colon ("I think we should do this: ship it") is NOT mistaken
 *     for a speaker label.
 *   - A leading timestamp ([00:12], (1:02:03), 00:00:05) is stripped and kept
 *     as the segment's startTime. So is a bracketed one between the name and
 *     the colon ("Name (05:05): text") — without this, the colon inside the
 *     time was read as the label colon and every line was dropped.
 *   - Lines with no label continue the current speaker, one segment per line.
 *   - Recurring speakers (same name, case-insensitive) merge into one, segments
 *     kept in transcript order. First-seen casing wins for the display name.
 *   - Lines before the first labeled turn are dropped.
 *
 * Pure + dependency-free so it's unit-testable and reusable (e.g. a future
 * server-side Fireflies ingestion could share it).
 */

export type ParsedSegment = { text: string; startTime: string | null };
export type ParsedSpeaker = { name: string; segments: ParsedSegment[] };

const LEADING_TIMESTAMP = /^[[(]?(\d{1,2}:\d{2}(?::\d{2})?)[\])]?\s+/;
const NAME_THEN_TIMESTAMP = /^(.+?)\s*[[(](\d{1,2}:\d{2}(?::\d{2})?)[\])]\s*:\s*(.*)$/;
const NAME_LIKE = /^[\p{L}][\p{L}\s.'-]{0,39}$/u;

function looksLikeName(candidate: string): boolean {
	if (!NAME_LIKE.test(candidate)) return false;
	return candidate.split(/\s+/).length <= 4;
}

export function parseLabeledTranscript(raw: string): ParsedSpeaker[] {
	const order: string[] = [];
	const byKey = new Map<string, ParsedSpeaker>();
	let currentKey: string | null = null;

	const speakerFor = (name: string): ParsedSpeaker => {
		const key = name.toLowerCase();
		let speaker = byKey.get(key);
		if (!speaker) {
			speaker = { name, segments: [] };
			byKey.set(key, speaker);
			order.push(key);
		}
		currentKey = key;
		return speaker;
	};

	for (const rawLine of raw.split("\n")) {
		const trimmed = rawLine.trim();
		if (!trimmed) continue;

		let rest = trimmed;
		let startTime: string | null = null;
		const ts = rest.match(LEADING_TIMESTAMP);
		if (ts) {
			startTime = ts[1] ?? null;
			rest = rest.slice(ts[0].length);
		}

		const labeled = rest.match(NAME_THEN_TIMESTAMP);
		if (labeled && looksLikeName((labeled[1] ?? "").trim())) {
			const speaker = speakerFor((labeled[1] ?? "").trim());
			const text = (labeled[3] ?? "").trim();
			if (text) speaker.segments.push({ text, startTime: startTime ?? labeled[2] ?? null });
			continue;
		}

		const colonIdx = rest.indexOf(":");
		if (colonIdx > 0) {
			const candidate = rest.slice(0, colonIdx).trim();
			const text = rest.slice(colonIdx + 1).trim();
			if (looksLikeName(candidate)) {
				const speaker = speakerFor(candidate);
				if (text) speaker.segments.push({ text, startTime });
				continue;
			}
		}

		// Continuation line — belongs to the current speaker.
		if (currentKey) {
			byKey.get(currentKey)?.segments.push({ text: rest, startTime });
		}
	}

	return order
		.map((k) => byKey.get(k))
		.filter((s): s is ParsedSpeaker => !!s && s.segments.length > 0);
}
